import { createContext } from 'react';

import {
  ActiveContact,
  AgentSnapshot,
  CallHistoryEntry,
  ConnectInitState,
  ContactAttributes,
  DirectoryEntry,
  DispositionTree,
  QueueSummary,
  RecordingState,
} from '../../../@types/connect.type';

// Snapshot of the just-ended call, surfaced so the disposition modal can
// post the agent's wrap-up selection to the backend after the call drops.
export interface PendingDisposition {
  contactId: string;
  initialContactId: string;
  // Channel the contact arrived on — used as part of the lambda key when
  // fetching the category tree (`<configName>.<channel>`).
  channel: string;
  // Queue name shown in the modal header for context.
  queueName: string | null;
  endedAt: string;
}

// Context value exposed by ConnectProvider. Consumers read state and trigger
// telephony actions through this single surface; no component should reach
// into `window.connect` directly.

export interface ConnectContextValue {
  initState: ConnectInitState;
  agent: AgentSnapshot | null;
  contact: ActiveContact | null;
  // Attributes attached by the IVR / CRM lookup for the active call. Empty
  // when no call is active.
  attributes: ContactAttributes;
  recordingState: RecordingState;
  // Set when a call ends and the agent has not yet completed the disposition
  // modal. Cleared after submission or skip.
  pendingDisposition: PendingDisposition | null;
  history: CallHistoryEntry[];
  directory: DirectoryEntry[];
  queues: QueueSummary[];
  // Whether the right-hand secondary drawer is open. Lifted into context so
  // both the sidebar widget and click-to-dial atoms can open it on demand.
  isPanelOpen: boolean;
  setPanelOpen: (open: boolean) => void;
  setStatus: (statusName: string) => Promise<void>;
  dial: (e164: string) => Promise<void>;
  hangup: () => Promise<void>;
  // Accept the currently-routed inbound voice contact (if any). Used by the
  // Queues panel pickup buttons; no-op when nothing is ringing.
  acceptCall: () => Promise<void>;
  pauseRecording: () => Promise<void>;
  resumeRecording: () => Promise<void>;
  fetchDispositionTree: (channel?: string) => Promise<DispositionTree>;
  submitDisposition: (
    contactId: string,
    initialContactId: string,
    categoryPath: string,
    notes: string
  ) => Promise<void>;
  // Dismiss the post-call wrap-up modal without submitting (e.g. agent
  // closed the panel; the prompt will reopen on next call end).
  dismissDisposition: () => void;
  popOut: () => void;
  addDirectoryEntry: (entry: Omit<DirectoryEntry, 'id' | 'createdAt'>) => void;
  updateDirectoryEntry: (id: string, patch: Partial<Omit<DirectoryEntry, 'id'>>) => void;
  removeDirectoryEntry: (id: string) => void;
  clearCallHistory: () => void;
}

// `null` sentinel signals "no provider in tree". useConnect throws when it
// sees this so misuse is caught immediately rather than silently no-op'ing.
export const ConnectContext = createContext<ConnectContextValue | null>(null);
